import { useEventConfig } from '@/shared/config/event-config-context';
import { cn } from '@/shared/lib/cn';

interface AudienceToggleProps {
  /** Trilha ativa — o mesmo valor que vai na query `perfil`. Vazio lista todas. */
  active: string;
  onChange: (profile: string) => void;
}

/** Nome vazio: sem `perfil` na query, a API devolve o grupo inteiro. */
const ALL_PROFILES = '';

/**
 * Alterna entre as trilhas do evento, com "Todos" à frente delas.
 *
 * As trilhas são os `profiles` da parametrização, na ordem que ela define: cada
 * uma é um valor do atributo de perfil do participante, e a listagem envia o nome
 * escolhido em `perfil`.
 *
 * "Todos" não é uma trilha — é a ausência do filtro, e por isso vale a lista
 * inteira. Existe porque o filtro por trilha é uma igualdade sobre o valor do
 * atributo de perfil no MICE: participante com esse atributo em branco, ou com
 * valor que não está em `profiles`, não casa NENHUMA trilha. Sem esta opção ele
 * não aparecia em aba nenhuma — e, não aparecendo, não podia ser aprovado.
 *
 * Aparece com um perfil ou mais, e não a partir de dois: com um só, a escolha
 * "aquela trilha ou todos" é justamente a que revela quem está fora dela. Sem
 * perfil nenhum não há filtro possível, e aí o seletor sairia da tela.
 */
export function AudienceToggle({ active, onChange }: AudienceToggleProps) {
  const { profiles } = useEventConfig();

  if (profiles.length === 0) return null;

  const options = [{ name: ALL_PROFILES, label: 'Todos' }, ...profiles];

  return (
    <div role="group" aria-label="Trilha do evento" className="inline-flex rounded-lg border border-line bg-surface p-0.5">
      {options.map(profile => {
        const isActive = profile.name === active;

        return (
          <button
            key={profile.name || 'todos'}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(profile.name)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[0.75rem] font-medium whitespace-nowrap transition-colors duration-150',
              isActive ? 'bg-brand-600 text-white' : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
            )}
          >
            {profile.label}
          </button>
        );
      })}
    </div>
  );
}
